/* Copyright (c) Trimble Inc. All rights reserved. */

import type { PropsWithChildren, ReactElement } from 'react';
import { useEffect } from 'react';
import React from 'react';
import clsx from 'clsx';
import useDropDownStyles from './Dropdown.style';
import { Box, Button, ListItem, ListItemProps, ListItemText, Menu, Typography } from '@material-ui/core';
import ModusIcon from 'legacy-components/icons/ModusIcon';

export interface IDropDownOption {
  value: string;
  title?: string | ReactElement;
  isOptGroup?: boolean;
  optType?: 'title' | 'itemBody' | 'itemHeader';
  type?: string;
}
interface DropDownViewerProps  {
  options: IDropDownOption[];
  handleOnSelect: (value: string) => void;
  defaultSelectorValue: IDropDownOption;
  disabled: boolean;
  itemHeaderIcon?: ReactElement;
  contentIcon?: ReactElement;
  startIcon?: ReactElement;
  endIcon?: ReactElement;
  placeholderText?: ReactElement | string;
}

/**
 * @component DropDown
 * @description  similar to a list box, that allows the user to choose one value from a list. When activated, it displays (drops down) a list of values, from which the user may select one. They can be used in menus and tabs.
 */
export const DropDown: React.FC<PropsWithChildren<DropDownViewerProps>> =
  React.memo(
    ({
      options,
      handleOnSelect,
      defaultSelectorValue,
      disabled,
      children,
      itemHeaderIcon,
      contentIcon,
      startIcon,
      endIcon,
      placeholderText = 'Choose one...',
      ...props
    }) => {
      const [toggle, setToggle] = React.useState(null);
      const [selectedValue, setSelectedValue] = React.useState<
        IDropDownOption | undefined
      >(defaultSelectorValue);
      const classes = useDropDownStyles();
      const handleClose = () => {
        setToggle(null);
      };
      const openMenu = (event: any) => {
        setToggle(event.currentTarget);
      };
      useEffect(() => {
        handleClose();
      }, [selectedValue]);
      return (
        <Box>
          {!disabled && (
            <Button
            fullWidth
            style={{justifyContent:"space-between"}}
              variant="text"
              className={clsx([{ [classes.button]: true }])}
              startIcon={startIcon}
              endIcon={
                <ModusIcon
                  name={'expand_more'}
                  className={classes.expandMoreIcon}
                />
              }
              onClick={openMenu}
            >
              <Typography className={classes.buttonTextContainer} variant="body2" color="secondary">
                <span className={classes.buttonText}>
                  {defaultSelectorValue?.title}
                </span>
                {!defaultSelectorValue?.title && (
                  <span className={classes.placeholder}>{placeholderText}</span>
                )}
              </Typography>
            </Button>
          )}

          <Menu
            anchorEl={toggle}
            open={Boolean(toggle)}
            onClose={handleClose}
            className={classes.menu}
          >
            {children && (
              <ListItem
                className={clsx([{ [classes.itemHeader]: true }])}
                // TODO: To be enabled in Q4 for Personal Space inclusion
                /* onClick={() => {
                  handleOnSelect(
                    options?.filter((option) => option.optType === 'title')[0]
                      ?.value
                  );
                  setSelectedValue(
                    options?.filter((option) => option.optType === 'title')[0]
                  );
                }}
              */
              >
                {children}
              </ListItem>
            )}

            {options?.map((option: IDropDownOption, index: number) => (
              <div key={index}>
                {option.optType === 'itemHeader' && (
                  <Box display="flex" flexDirection="column" justifyItems="space-between">
                    <ListItem
                      className={clsx([{ [classes.itemHeader]: true }])}
                      disabled
                    >
                      <ListItemText>
                        <Typography
                          variant="h6"
                          style={{ wordWrap: 'break-word', paddingRight: "10px" }}
                         
                        >
                          {option.title ?? option.value ?? ''}
                        </Typography>
                      </ListItemText>
                      {itemHeaderIcon && <div>{itemHeaderIcon}</div>}
                    </ListItem>
                  </Box>
                )}
                {option.optType === 'itemBody' && (
                  <ListItem
                    className={clsx([{ [classes.itemBody]: true }])}
                    onClick={() => {
                      handleOnSelect(option?.value);
                      setSelectedValue(option);
                    }}
                  >
                    {contentIcon && (
                      <div
                        className={clsx([
                          classes.contentIcon,
                          {
                            [classes.selectedIcon]:
                              selectedValue?.value === option.value,
                          },
                        ])}
                      >
                        {contentIcon}
                      </div>
                    )}
                    <ListItemText>
                      <Box display= "flex"  flexDirection={'row'} alignItems="center">
                        <Typography
                          variant="body2"
                          style={{
                            color:
                            selectedValue?.value === option.value
                              ? 'primary'
                              : 'secondary',
                              paddingRight:"10px"
                          }}
                        
                        >
                          {option.title ?? option.value ?? ''}
                        </Typography>
                      </Box>
                    </ListItemText>
                  </ListItem>
                )}
              </div>
            ))}
          </Menu>
        </Box>
      );
    }
  );
